import { cva, type VariantProps } from 'class-variance-authority'
import { ArrowRight, CircleAlert, CircleCheck } from 'lucide-react'
import type { ComponentProps, CSSProperties } from 'react'
import { useRef, useState } from 'react'
import { useDynamicInputSize } from '@/src/hooks/use-dynamic-input-size'
import { cn } from '@/src/utils/styles'

const inputVariants = cva(
  'flex items-center bg-transparent text-input-text file:bg-transparent file:border-0 focus-visible:outline-none disabled:opacity-50 disabled:pointer-events-none transition-all',
  {
    variants: {
      dimension: {
        small: 'h-8 rounded-[8px] py-1.5 px-3 text-sm leading-5',
        medium: 'h-12 rounded-xl py-3 px-4',
        large: 'h-14 rounded-2xl py-4 px-5',
      },
      variant: {
        default:
          'outline outline-1 outline-input-border placeholder:text-input-placeholder hover:outline-2 hover:outline-input-border-hover hover:placeholder:text-input-placeholder-hover focus-visible:ring-2 focus-visible:ring-input-border-focus focus-visible:placeholder:text-input-border',
        ghost: 'p-0 rounded-none',
      },
    },
    defaultVariants: {
      dimension: 'small',
      variant: 'default',
    },
  },
)

interface InputProps
  extends ComponentProps<'input'>,
    VariantProps<typeof inputVariants> {
  autoScale?: boolean
  isValid?: boolean
  isInvalid?: boolean
  containerClassName?: string
  onNextCta?: () => void
}

const Input = ({
  className,
  type,
  dimension,
  variant,
  value,
  autoScale,
  style,
  ref,
  isValid,
  isInvalid,
  containerClassName,
  onNextCta,
  ...props
}: InputProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const { fontSize } = useDynamicInputSize({
    containerRef,
    inputValue: (value?.toString() ?? '') as string,
  })
  const [isFocused, setIsFocused] = useState(false)

  return (
    <div
      ref={containerRef}
      className={cn(
        inputVariants({ dimension, variant, className }),
        {
          'outline-danger-text hover:outline-danger-text': isInvalid,
          'ring-2 ring-input-border-focus placeholder:text-input-border hover:!outline-none':
            isFocused && variant !== 'ghost',
          'opacity-50 pointer-events-none': props.disabled,
        },
        'flex items-center gap-2 w-full relative',
        containerClassName,
      )}
    >
      <input
        type={type}
        className='w-full bg-transparent outline-none'
        ref={ref}
        value={value}
        style={autoScale ? ({ fontSize, ...style } as CSSProperties) : style}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        {...props}
      />
      {onNextCta && (
        <button
          type='button'
          aria-label='Next'
          onClick={onNextCta}
          className='min-w-8 h-8 rounded-full bg-button-primary flex items-center justify-center hover:bg-button-primary-hover'
        >
          <ArrowRight className='text-button-primary-text w-4 h-4' />
        </button>
      )}
      {isValid && (
        <div className='pointer-events-none'>
          <CircleCheck className='text-success-text w-6 h-6' />
        </div>
      )}
      {isInvalid && (
        <div className='pointer-events-none'>
          <CircleAlert className='text-danger-text w-6 h-6' />
        </div>
      )}
    </div>
  )
}

Input.displayName = 'Input'

export { Input }
